/**
 * ActionList Component
 *
 * Dropdown menu that portals to document.body to escape table cell overflow constraints.
 * Uses context-based registration pattern - items register on mount, unregister on unmount.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import ReactDOM from 'react-dom';
import classnames from 'classnames';

interface ActionListContextType {
  register: () => () => void;
}

const ActionListContext = createContext<ActionListContextType>({
  register: () => () => {},
});

interface ActionListProps {
  title: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
}

interface ActionListComponent extends React.FC<ActionListProps> {
  Item: typeof Item;
}

const ActionList: ActionListComponent = ({ title, className, children }) => {
  const [visible, setVisible] = useState(false);
  const [showPortal, setShowPortal] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [itemCount, setItemCount] = useState(0);
  const buttonRef = useRef<HTMLDivElement>(null);
  const portalRef = useRef<HTMLDivElement>(null);

  const register = useCallback(() => {
    setItemCount((c) => c + 1);
    return () => setItemCount((c) => c - 1);
  }, []);

  const contextValue = useMemo(() => ({ register }), [register]);

  // Calculate position and show portal after one frame
  useEffect(() => {
    if (!visible || !buttonRef.current) {
      setShowPortal(false);
      return;
    }

    const rect = buttonRef.current.getBoundingClientRect();
    const leftOffset = className?.includes('left') ? 0 : -70;

    setPosition({
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX + leftOffset,
    });

    // Delay showing portal by one frame to prevent flash from top-left
    requestAnimationFrame(() => {
      setShowPortal(true);
    });
  }, [visible, className]);

  // Close menu on resize
  useEffect(() => {
    if (!visible) return;

    const handleResize = () => setVisible(false);
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [visible]);

  // Handle clicks outside
  useEffect(() => {
    if (!visible) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        portalRef.current &&
        !portalRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target)
      ) {
        setVisible(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [visible]);

  return (
    <ActionListContext.Provider value={contextValue}>
      <div
        ref={buttonRef}
        className={classnames('action-list', className, { visible })}
        onClick={() => setVisible(true)}
        style={{ display: itemCount > 0 ? undefined : 'none' }}
      >
        <span className="action-list-title">{title}</span>
      </div>
      {ReactDOM.createPortal(
        <div
          ref={portalRef}
          className="action-list-portal mt-10"
          style={{
            position: 'absolute',
            top: `${position.top}px`,
            left: `${position.left}px`,
            zIndex: 1,
            display: showPortal ? 'block' : 'none',
          }}
        >
          <ul
            className="actions-list-items"
            onClick={(e) => {
              e.stopPropagation();
              setVisible(false);
            }}
          >
            {children}
          </ul>
        </div>,
        document.body
      )}
    </ActionListContext.Provider>
  );
};

const Item = ({
  className,
  children,
}: {
  className?: string;
  children?: React.ReactNode;
}) => {
  const { register } = useContext(ActionListContext);

  useEffect(() => register(), [register]);

  return (
    <li className={classnames('action-list-item', className)}>{children}</li>
  );
};

ActionList.Item = Item;
export default ActionList;
